import { supabase } from "../supabaseClient"
import { normalizeUrl, sanitizeUserTextInput } from "./security"

const SESSION_THRESHOLD = 100

const normalizeHostname = (value) => {
  if (typeof value !== "string") return ""
  return value.trim().toLowerCase()
}

const parseSessionCount = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const hostnameToUrl = (hostname) => {
  if (!hostname) return null
  const trimmed = hostname.replace(/^https?:\/\//i, "").trim()
  if (!trimmed) return null
  return normalizeUrl(`https://${trimmed}`)
}

const buildAccountName = (property, displayName) => {
  const candidates = [
    typeof property?.property_name === "string"
      ? property.property_name
      : null,
    typeof displayName === "string" ? displayName : null,
    typeof property?.property_id === "string"
      ? property.property_id.split("/").pop()
      : null,
    "GA4 Account",
  ]

  for (const candidate of candidates) {
    if (!candidate) continue
    const { sanitized } = sanitizeUserTextInput(candidate, { maxLength: 120 })
    if (sanitized) return sanitized
  }

  return "GA4 Account"
}

const buildHostnameSet = (hostnameAuditData) => {
  const hostSet = new Set()
  const hostUrlMap = new Map()

  if (!Array.isArray(hostnameAuditData)) {
    return { hostSet, hostUrlMap }
  }

  for (const entry of hostnameAuditData) {
    const normalized = normalizeHostname(entry?.hostname)
    if (!normalized) continue
    const sessions = parseSessionCount(
      entry?.sessions ?? entry?.session_count ?? entry?.sessionCount,
    )
    if (sessions <= SESSION_THRESHOLD) continue
    hostSet.add(normalized)
    if (!hostUrlMap.has(normalized)) {
      const url = hostnameToUrl(normalized)
      if (url) hostUrlMap.set(normalized, url)
    }
  }

  return { hostSet, hostUrlMap }
}

const assignAccountToProperty = async (propertyId, accountId) => {
  const { error } = await supabase
    .from("ga4_properties")
    .update({ account_id: accountId })
    .eq("id", propertyId)

  if (!error) {
    return { limitReached: false }
  }

  const message = error.message || ""
  if (message.includes("maximum number of GA4 properties")) {
    return { limitReached: true }
  }

  return { error }
}

const createAccountForProperty = async (property, displayName) => {
  const name = buildAccountName(property, displayName)
  const { data, error } = await supabase
    .from("accounts")
    .insert({
      name,
      organization_id: property.organization_id,
    })
    .select("id")
    .single()

  if (error) throw error
  if (!data?.id) {
    throw new Error("Failed to create account for GA4 property")
  }

  return data.id
}

const ensureAccountWebsites = async (accountId, organizationId, hostUrlMap) => {
  if (!accountId || !organizationId || hostUrlMap.size === 0) return []

  const { data: existingRows, error: existingError } = await supabase
    .from("website_urls")
    .select("id, url")
    .eq("account_id", accountId)

  if (existingError) throw existingError

  const existing = new Set(
    (existingRows || [])
      .map((row) => (typeof row?.url === "string" ? row.url.toLowerCase() : ""))
      .filter(Boolean),
  )

  const inserts = []
  for (const url of hostUrlMap.values()) {
    const normalized = typeof url === "string" ? url.toLowerCase() : ""
    if (!normalized || existing.has(normalized)) continue
    inserts.push({
      url,
      organization_id: organizationId,
      account_id: accountId,
    })
  }

  if (!inserts.length) return []

  const { error: insertError } = await supabase
    .from("website_urls")
    .insert(inserts)

  if (insertError) {
    const message = insertError.message || ""
    if (message.includes("maximum number of URLs")) {
      console.warn(
        "[GA4 Accounts] Reached website limit while adding hostnames",
        {
          accountId,
          organizationId,
          attemptedUrls: inserts.map((entry) => entry.url),
        },
      )
      return []
    }
    throw insertError
  }

  return inserts.map((entry) => entry.url)
}

export async function syncGa4AccountForProperty({
  property,
  propertyDisplayName,
  hostnameAuditData,
}) {
  if (!property?.id || !property?.organization_id) {
    return {
      accountId: property?.account_id || null,
      createdAccount: false,
      matchedExistingAccount: false,
      updatedProperty: false,
      addedWebsiteUrls: [],
    }
  }

  const { hostUrlMap } = buildHostnameSet(hostnameAuditData)

  let accountId = property.account_id || null
  let updatedProperty = false
  let createdAccount = false

  if (!accountId) {
    const newAccountId = await createAccountForProperty(
      property,
      propertyDisplayName,
    )
    const assignment = await assignAccountToProperty(property.id, newAccountId)
    if (assignment.error) throw assignment.error
    accountId = newAccountId
    updatedProperty = true
    createdAccount = true
  }

  const addedWebsiteUrls = await ensureAccountWebsites(
    accountId,
    property.organization_id,
    hostUrlMap,
  )

  return {
    accountId,
    createdAccount,
    matchedExistingAccount: false,
    updatedProperty,
    addedWebsiteUrls,
  }
}

const normalizeAccountIds = (accountIds) => {
  if (!accountIds) return []
  if (typeof accountIds === "string") {
    return accountIds ? [accountIds] : []
  }
  if (Array.isArray(accountIds)) {
    return accountIds
      .filter((id) => typeof id === "string" && id.trim())
      .map((id) => id.trim())
  }
  if (accountIds instanceof Set) {
    return Array.from(accountIds)
      .filter((id) => typeof id === "string" && id.trim())
      .map((id) => id.trim())
  }
  return []
}

export async function deleteAccountsIfEmpty(accountIds, supabaseClient = supabase) {
  const normalizedIds = Array.from(new Set(normalizeAccountIds(accountIds)))
  if (normalizedIds.length === 0) return []

  const { data: remainingRows, error: remainingError } = await supabaseClient
    .from("ga4_properties")
    .select("account_id")
    .in("account_id", normalizedIds)

  if (remainingError) {
    console.error("[GA4 Accounts] Failed to check account usage", remainingError)
    return []
  }

  const remainingAccounts = new Set(
    (Array.isArray(remainingRows) ? remainingRows : [])
      .map((row) => (typeof row?.account_id === "string" ? row.account_id : null))
      .filter(Boolean),
  )

  const orphanedAccounts = normalizedIds.filter((id) => !remainingAccounts.has(id))
  if (orphanedAccounts.length === 0) return []

  const { error: deleteError } = await supabaseClient
    .from("accounts")
    .delete()
    .in("id", orphanedAccounts)

  if (deleteError) {
    console.error("[GA4 Accounts] Failed to delete orphaned accounts", {
      error: deleteError,
      accountIds: orphanedAccounts,
    })
    return []
  }
  return orphanedAccounts
}

export const __internal = {
  normalizeHostname,
  parseSessionCount,
  hostnameToUrl,
  buildHostnameSet,
  buildAccountName,
  normalizeAccountIds,
}
